import type {EmailStockPopupRequest} from './email-stock-media-popup.types';

/**
 * Tiny module-level mediator so the media-modals library (plain functions, callable from GrapesJS) can open the
 * email editor's stock-media popup. Mirrors the poster editor's `stock-media-popup-controller`. The popup anchors
 * to the left (blocks) panel, which registers its root element here on mount.
 */

let activeOpener: ((request: EmailStockPopupRequest) => void) | null = null;
let activeCloser: (() => void) | null = null;
let pendingRequest: EmailStockPopupRequest | null = null;
let anchorElement: HTMLElement | null = null;
let isOpen = false;
const openStateListeners = new Set<() => void>();

function setIsOpen(open: boolean): void {
  if (isOpen === open) {
    return;
  }
  isOpen = open;
  openStateListeners.forEach((listener) => {
    listener();
  });
}

/** Subscribe to open/close transitions of the popup. Pairs with `isEmailStockMediaPopupOpen` for `useSyncExternalStore`. */
export function subscribeToEmailStockPopupOpenState(listener: () => void): () => void {
  openStateListeners.add(listener);
  return (): void => {
    openStateListeners.delete(listener);
  };
}

export function isEmailStockMediaPopupOpen(): boolean {
  return isOpen;
}

export function registerEmailStockMediaPopupHandlers(opener: (request: EmailStockPopupRequest) => void, closer: () => void): () => void {
  activeOpener = opener;
  activeCloser = closer;
  if (pendingRequest) {
    opener(pendingRequest);
    pendingRequest = null;
  }
  return (): void => {
    if (activeOpener === opener) {
      activeOpener = null;
      setIsOpen(false);
    }
    if (activeCloser === closer) {
      activeCloser = null;
    }
  };
}

/** The left panel registers its root element; the popup closes if the anchor unmounts (e.g. viewport shrinks). */
export function registerEmailStockPopupAnchor(element: HTMLElement): () => void {
  anchorElement = element;
  return (): void => {
    if (anchorElement === element) {
      anchorElement = null;
      closeEmailStockMediaPopup();
    }
  };
}

export function getEmailStockPopupAnchor(): HTMLElement | null {
  return anchorElement;
}

export function openEmailStockMediaPopup(request: EmailStockPopupRequest): void {
  setIsOpen(true);
  if (activeOpener) {
    activeOpener(request);
    return;
  }
  // Host hasn't registered yet (it mounts asynchronously); buffer the most recent request and replay on register.
  pendingRequest = request;
}

export function closeEmailStockMediaPopup(): void {
  setIsOpen(false);
  pendingRequest = null;
  activeCloser?.();
}
